'use client';

import Button from '@/components/Button';
import Image from '@/components/Image';
import PopupResponsive from '@/components/PopupResponsive';
import Upload from '@/components/Upload';
import { env } from '@/utils/env';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

interface UploadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onFileUpload: (fileUrl: string) => void;
  onClickUpload: (buttonType: 'Upload' | 'Photo') => void;
  onStartUpload?: () => void;
}

export default function UploadDialog({
  isOpen,
  onClose,
  onFileUpload,
  onClickUpload,
  onStartUpload,
}: UploadDialogProps) {
  const t = useTranslations('lens-process');
  const isWap = useMediaQuery('(max-width:1280px)');
  const [showHowTo, setShowHowTo] = useState(false);
  const sampleImageMap = {
    fr: '/lens-process/pres_sample-fr.png',
    de: '/lens-process/pres_sample-de.png',
    default: '/lens-process/pres_sample.png',
  };
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // 处理上传图片按钮的上传状态
  const onUploadImageChange = (isUploading: boolean) => setIsUploadingImage(isUploading);
  // 处理拍照按钮的上传状态
  const onUploadPhotoChange = (isUploading: boolean) => setIsUploadingPhoto(isUploading);

  return (
    <PopupResponsive
      open={isOpen}
      onClose={onClose}
      showDivider={false}
      closeOnClickOverlay={false}
      height="80vh"
      bodyClassName="!pt-[10px]"
      title={t('scanYourPrescription')}
    >
      <div className="pb-[40px]">
        <p className="text-sm mb-[16px]">
          {t('useYourCameraToTakeAPhotOfYourWrittenPrescriptionOrUploadAnImage')}
        </p>

        <div className="mb-[16px] flex justify-center">
          <Image
            src={sampleImageMap[env.NEXT_PUBLIC_SITE_NAME] || sampleImageMap.default}
            alt={t('prescriptionSample')}
            width={588}
            height={186}
            className="rounded-lg w-[347px] h-[138px] xl:w-[588px] xl:h-[186px]"
          />
        </div>

        <div className="bg-[#fff9d7] p-[12px] rounded-lg mb-[16px]">
          <p className="font-bs f14">{t('havePrismValues')}</p>
          <p className="f12 leading-[18px] mt-[8px]">
            {t('theScanCannotCapturePrismValuesPleaseManuallyInputBasedOnYourPrescriptionIfNoted')}
          </p>
        </div>
        {/* How to obtain accurate scans */}
        <p
          className="mt-[24px] text-center underline f12 cursor-pointer"
          onClick={() => {
            setShowHowTo(!showHowTo);
            onClickUpload('Upload');
          }}
        >
          {t('howToObtainAccurateScans')}
        </p>
        {showHowTo && (
          <div className="mt-[17px] p-[16px_12px] bg-[#f5f6f7] mb-[40px]">
            <p className="font-bs f14">{t('placeOnAFlatSurface')}</p>
            <p className="mt-[10px] f12 leading-[18px] text-[grey]">
              {t('ifYouAreUsingAPhonePlaceThePrescriptionOnAFlatSurfaceAndSmoothOutAnyCreases')}
            </p>
            <p className="mt-[16px] font-bs f14">{t('takeAClearPhoto')}</p>
            <p className="mt-[10px] f12 leading-[18px] text-[grey]">
              {t(
                'whenUploadingImagesPleaseEnsureThatTheTextOnThePrescriptionRemainsHorizontalAndLegibleThePhotoIsClearAndAllPrescriptionValuesAreVisibleWithinTheImage',
              )}
            </p>
            <p className="mt-[16px] font-bs f14">{t('holdOnToYourPrescription')}</p>
            <p className="mt-[10px] f12 leading-[18px] text-[grey]">
              {t('keepYourPrescriptionNearbySoYouCanEasilyCheckTheScannedValues')}
            </p>
          </div>
        )}

        <div className="flex justify-center gap-[14px] absolute bottom-0 left-0 right-0 h-[69px] p-[14px] bg-white max-xl:shadow-[0px_-1px_5px_0px_rgba(204,204,204,0.5)]">
          <Upload
            sceneId={2}
            fileType="image"
            accept="image/jpeg,image/png,image/jpg"
            className="!mt-0 flex-1 max-w-[380px]"
            toastConfig={{ success: false, fail: true }}
            sx={{
              '& .MuiBox-root': {
                width: '100%',
                height: '40px',
              },
            }}
            onClickUpload={() => {
              onClickUpload('Upload');
            }}
            onStartUpload={onStartUpload}
            onChange={onFileUpload}
            onUploadChange={onUploadImageChange}
          >
            <Button
              variant={isWap ? 'outlined' : 'contained'}
              fullWidth
              loading={isUploadingImage}
              className="!font-bs !h-[40px]"
              sx={{
                '&.MuiButton-root': {
                  maxWidth: '380px',
                },
              }}
            >
              {t('uploadImage')}
            </Button>
          </Upload>

          {isWap && (
            <Upload
              sceneId={2}
              fileType="image"
              accept="image/jpeg,image/png,image/jpg"
              className="!mt-0 flex-1"
              toastConfig={{ success: false, fail: true }}
              sx={{
                '& .MuiBox-root': {
                  width: '100%',
                  height: '40px',
                },
              }}
              capture="environment"
              onChange={onFileUpload}
              onClickUpload={() => {
                onClickUpload('Photo');
              }}
              onStartUpload={onStartUpload}
              onUploadChange={onUploadPhotoChange}
            >
              <Button
                loading={isUploadingPhoto}
                variant="contained"
                fullWidth
                className="!font-bs !h-[40px]"
              >
                {t('takePhoto')}
              </Button>
            </Upload>
          )}
        </div>
      </div>
    </PopupResponsive>
  );
}
